/**
 * The ladder's sound-alike and say-it cards (v1 ticket 14). Sound-alike is
 * step 1's listening card: the lesson plays the word (its audioText) and the
 * learner picks it from two words one sound apart. Say it from the meaning is
 * step 2's: the meaning shows, the learner says the word, and the review's
 * pronunciation check (or "I said it") settles it; the word shows only then.
 */
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { SayFromMeaningApi, SoundAlikeApi } from '../api';
import { ExerciseElement } from '../base';
import { t } from '../../i18n';
import { isolate } from './ladder-cards';
import { iso, ui, uiDir } from '../../bidi-html';
import { signHostOf, signZone } from '../sign';
import { giveUpButton, giveUpOffered } from '../give-up';

// ---- SoundAlike -------------------------------------------------------------

@customElement('li-soundalike')
export class LiSoundAlike extends ExerciseElement<typeof SoundAlikeApi> {
  protected readonly api = SoundAlikeApi;
  @state() private picked: number | null = null;
  protected resetState() { this.picked = null; }

  render() {
    const p = this.controller?.props;
    if (!p) return nothing;
    const done = this.picked !== null;
    const right = this.picked === p.correct;
    const answer = isolate(String(p.options[p.correct] ?? ''));
    const sign = signHostOf(this);
    const buttons = p.options.map((o, i) => {
      const d = isolate(String(o));
      return html`
        <button
          class="option ca ${!sign && done && i === p.correct ? 'correct' : ''} ${!sign && done && i === this.picked && !right ? 'wrong' : ''}"
          ?disabled=${done}
          dir=${d.dir}
          lang=${d.lang}
          @click=${() => this.pick(i)}
        >${o}</button>`;
    });
    const explain = p.explain ? html`<div class="note" dir=${uiDir(p.explain)}>${iso(p.explain)}</div>` : nothing;
    const verdict = (withExplain: boolean) => html`<div class="result ${right ? 'ok' : 'bad'}" dir=${ui()}>
        ${right
          ? t('ex.correct')
          : html`${t('ex.answerIs')} <bdi class="ca" dir=${answer.dir} lang=${answer.lang}>${p.options[p.correct]}</bdi>`}
        ${withExplain ? explain : nothing}
      </div>`;
    const giveUp = !done && giveUpOffered(this) ? giveUpButton(this.giveUp) : nothing;
    if (sign) {
      // The word is only heard (the tag's play button); the pair is picked in the thumb zone, and the word with its
      // meaning joins the bubble once answered.
      return html`
        <div class="ex soundalike sign">
          ${done && p.explain ? html`<div class="sign-translation sign-fade" dir=${uiDir(p.explain)}>${iso(p.explain)}</div>` : nothing}
          ${signZone(sign, done
            ? html`<div class="sign-fade">${verdict(false)}</div>`
            : html`<div class="sign-prompt sign-fade" dir=${ui()}>${t('ex.soundalike')}</div><div class="options alike-pair">${buttons}</div>${giveUp}`)}
        </div>`;
    }
    return html`
      <div class="ex soundalike">
        <div class="label">${t('ex.soundalike')}</div>
        <div class="options alike-pair">${buttons}</div>
        ${giveUp}
        ${done ? verdict(true) : nothing}
      </div>
    `;
  }

  private pick(i: number) {
    const p = this.controller?.props;
    if (!p || this.picked !== null) return;
    this.picked = i;
    this.send('answer', { value: p.options[i], correct: i === p.correct, expected: p.options[p.correct] });
  }

  /** "I don't know — show me" (ticket 46): no word is picked (-1), the right one shows, a wrong answer is sent. */
  private giveUp = () => {
    const p = this.controller?.props;
    if (!p || this.picked !== null) return;
    this.picked = -1;
    this.send('answer', { value: '', correct: false, gaveUp: true, expected: p.options[p.correct] });
  };
}
export const SoundAlike = { ...SoundAlikeApi, tagName: 'li-soundalike' };

// ---- SayFromMeaning ---------------------------------------------------------

@customElement('li-sayfrommeaning')
export class LiSayFromMeaning extends ExerciseElement<typeof SayFromMeaningApi> {
  protected readonly api = SayFromMeaningApi;
  /** The word is on screen: after "I said it", or once the check has judged it. */
  @state() private shown = false;
  @state() private said = false;
  /** The view's pronunciation check is in flight (`data-checking`): "I don't know" waits for its verdict. */
  @property({ type: Boolean, attribute: 'data-checking' }) checking = false;
  protected resetState() { this.shown = false; this.said = false; }

  render() {
    const p = this.controller?.props;
    if (!p) return nothing;
    const word = isolate(p.ca);
    const latin = p.latin ? isolate(p.latin) : null;
    const sign = signHostOf(this);
    const meaning = html`<div class="say-meaning" dir=${uiDir(p.en)}>${iso(p.en)}</div>`;
    const wordEl = this.shown
      ? html`<div class="say-word">
          ${latin
            ? html`<bdi class="big-ca" dir=${latin.dir} lang=${latin.lang}>${p.latin}</bdi>
                <div><bdi class="ca muted small" dir=${word.dir} lang=${word.lang}>${p.ca}</bdi></div>`
            : html`<bdi class="big-ca" dir=${word.dir} lang=${word.lang}>${p.ca}</bdi>`}
        </div>`
      : nothing;
    const button = html`<div class="actions">
        <button class="btn primary say-ready" ?disabled=${this.said || this.shown} @click=${this.ready}>${t('ex.isaidit')}</button>
      </div>`;
    const giveUp = !this.shown && !this.checking && giveUpOffered(this) ? giveUpButton(this.giveUp) : nothing;
    if (sign) {
      // The meaning is the bubble (the word joins it once judged); the ask and its button sit in the thumb zone,
      // the pronunciation check under them.
      return html`
        <div class="ex sayit sayfrommeaning sign">
          ${meaning}${wordEl}
          ${signZone(sign, this.shown ? nothing : html`<div class="sign-prompt sign-fade" dir=${ui()}>${t('ex.sayfrommeaning')}</div>${button}${giveUp}`)}
        </div>`;
    }
    return html`
      <div class="ex sayit sayfrommeaning">
        <div class="label">${t('ex.sayfrommeaning')}</div>
        ${meaning}${wordEl}${button}${giveUp}
      </div>
    `;
  }

  /** Show the word: the review calls this once the pronunciation check has judged it. */
  reveal() { this.shown = true; }

  /** "I don't know — show me" (ticket 46): the word shows, no check is made, and a wrong answer is sent. */
  private giveUp = () => {
    const p = this.controller?.props;
    if (!p || this.said || this.shown) return;
    this.said = true;
    this.shown = true;
    this.send('answer', { value: '', correct: false, gaveUp: true, expected: p.ca });
  };

  private ready = () => {
    if (this.said || this.shown) return;
    this.said = true;
    this.shown = true;
    this.send('ready', { ca: this.controller?.props?.ca });
  };
}
export const SayFromMeaning = { ...SayFromMeaningApi, tagName: 'li-sayfrommeaning' };
